import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface ResultStepProps {
  onContinue: () => void;
}

const CLIPS = [
  {
    id: 1,
    title: "제가 그 종교에서 빠져나올 수 있었던 이유는요...",
    timestamp: "08:14",
    duration: "0:47",
    score: 96,
    viralScore: 9.4,
    peak: "채팅 속도 1,847msg/min",
    reason: "연속 4킬 달성과 함께 채팅 밀도가 전체 방송 최고치를 기록한 구간",
    thumbnail: "https://static.readdy.ai/image/7e3352e848b93c3eb9a2ea481cb871cb/2af2b4c3df902c350c994245c41ee4f6.png",
    tags: ["최고 점수", "YouTube Shorts"],
    color: "#00D9FF",
  },
  {
    id: 2,
    title: "여자들이 조건 봐서 짜증 난 거잖아",
    timestamp: "17:52",
    duration: "1:04",
    score: 89,
    viralScore: 8.7,
    peak: "시청자 급증 +12,400명",
    reason: "팀 전멸 직전 역전 상황에서 시청자 유입이 가장 빠르게 증가한 구간",
    thumbnail: "https://static.readdy.ai/image/7e3352e848b93c3eb9a2ea481cb871cb/8d713cf9c07859a905c93058f0f1b29e.png",
    tags: ["YouTube Shorts"],
    color: "#10B981",
  },
  {
    id: 3,
    title: "이럴거면 왜 왔니?",
    timestamp: "44:37",
    duration: "0:38",
    score: 81,
    viralScore: 7.9,
    peak: "참여 점수 피크 +43%",
    reason: "경기 종료 후 MVP 선수 인터뷰 중 예상치 못한 반응으로 참여율 급등",
    thumbnail: "https://static.readdy.ai/image/7e3352e848b93c3eb9a2ea481cb871cb/b41bbc7fd26798ed2de29ebd19b18bac.png",
    tags: ["YouTube Shorts"],
    color: "#F59E0B",
  },
];

const TIMELINE_PEAKS = [
  { x: 17, height: 35, active: false },
  { x: 22, height: 55, active: false },
  { x: 29, height: 96, active: true, label: "08:14" },
  { x: 37, height: 42, active: false },
  { x: 45, height: 89, active: true, label: "17:52" },
  { x: 53, height: 38, active: false },
  { x: 61, height: 50, active: false },
  { x: 68, height: 30, active: false },
  { x: 75, height: 60, active: false },
  { x: 82, height: 44, active: false },
  { x: 88, height: 81, active: true, label: "44:37" },
  { x: 94, height: 28, active: false },
];

export default function ResultStep({ onContinue }: ResultStepProps) {
  const [selectedClip, setSelectedClip] = useState(0);
  const [showInsights, setShowInsights] = useState(true);
  const clip = CLIPS[selectedClip];
  const navigate = useNavigate();

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#10B981]/20">
              <i className="ri-check-line text-[#10B981] text-sm" />
            </div>
            <span className="text-[#10B981] text-sm font-semibold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              처리 완료 · 3개 클립 생성됨
            </span>
          </div>
          <h2 className="text-3xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            AI가 생성한 하이라이트 클립
          </h2>
          <p className="text-gray-400 text-sm mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>
            47:22 방송 → 3개 숏폼 클립 (최고 바이럴 점수 9.4)
          </p>
        </div>
        <button
          onClick={() => setShowInsights(!showInsights)}
          className={`flex items-center gap-2 text-sm px-4 py-2 rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
            showInsights
              ? "bg-[#00D9FF]/10 border-[#00D9FF]/40 text-[#00D9FF]"
              : "bg-white/5 border-white/10 text-gray-400 hover:text-white"
          }`}
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-brain-line" />
          </span>
          AI 인사이트 {showInsights ? "숨기기" : "보기"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Preview + Timeline */}
        <div className="lg:col-span-2 space-y-4">
          {/* Video preview */}
          <div className="relative bg-[#050709] rounded-2xl overflow-hidden aspect-video border border-white/8">
            <img
              src={clip.thumbnail}
              alt={clip.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050709]/70 via-transparent to-transparent" />
            {/* Play button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 cursor-pointer transition-all duration-200">
                <i className="ri-play-fill text-white text-2xl ml-1" />
              </div>
            </div>
            {/* Clip info overlay */}
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
              <div>
                <h3 className="text-white font-bold text-lg leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{clip.title}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-gray-300 text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    <i className="ri-time-line mr-1" />{clip.timestamp} · {clip.duration}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: clip.color }}>{clip.score}</div>
                <div className="text-gray-400 text-xs">참여 점수</div>
              </div>
            </div>
          </div>

          {/* Engagement timeline */}
          <div className="bg-[#0F1419] border border-white/8 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <span className="text-white text-sm font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>참여도 타임라인</span>
              <span className="text-gray-500 text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>47:22 전체 방송</span>
            </div>
            <div className="relative h-20 flex items-end gap-0.5">
              {TIMELINE_PEAKS.map((bar, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-t relative cursor-pointer group"
                  style={{
                    height: `${bar.height}%`,
                    background: bar.active
                      ? `linear-gradient(180deg, ${bar.height >= 90 ? "#00D9FF" : bar.height >= 85 ? "#10B981" : "#F59E0B"}, ${bar.height >= 90 ? "#00D9FF" : bar.height >= 85 ? "#10B981" : "#F59E0B"}88)`
                      : "rgba(255,255,255,0.06)",
                    boxShadow: bar.active ? `0 0 8px ${bar.height >= 90 ? "#00D9FF" : bar.height >= 85 ? "#10B981" : "#F59E0B"}40` : "none",
                  }}
                >
                  {bar.active && bar.label && (
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: bar.height >= 90 ? "#00D9FF" : bar.height >= 85 ? "#10B981" : "#F59E0B" }}>
                      {bar.label}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-gray-600 text-xs mt-2" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              <span>00:00</span><span>15:00</span><span>30:00</span><span>47:22</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-3 gap-3">
            <button className="flex items-center justify-center gap-2 bg-[#00D9FF] hover:bg-[#00D9FF]/90 text-[#0A0E14] font-bold text-sm py-3 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-download-2-line" /></span>
              다운로드
            </button>
            <button
              onClick={() => navigate("/editor")}
              className="flex items-center justify-center gap-2 bg-white/8 hover:bg-white/12 text-white font-semibold text-sm py-3 rounded-xl border border-white/10 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-scissors-cut-line" /></span>
              편집하기
            </button>
            <button
              onClick={onContinue}
              className="relative flex items-center justify-center gap-2 bg-white/5 text-gray-500 font-semibold text-sm py-3 rounded-xl border border-white/8 cursor-pointer whitespace-nowrap overflow-hidden"
            >
              <span className="absolute inset-0 flex items-center justify-center bg-[#0F1419]/80 backdrop-blur-sm rounded-xl gap-2">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-lock-line text-gray-500 text-sm" /></span>
                <span className="text-gray-400 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>게시하기</span>
              </span>
            </button>
          </div>
        </div>

        {/* Right: Clip list + Insights */}
        <div className="space-y-4">
          {/* Clip list */}
          <div className="space-y-2">
            {CLIPS.map((c, i) => (
              <button
                key={c.id}
                onClick={() => setSelectedClip(i)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer text-left ${
                  selectedClip === i
                    ? "border-[#00D9FF]/50 bg-[#00D9FF]/8"
                    : "border-white/8 bg-[#0F1419] hover:border-white/15"
                }`}
              >
                <div className="w-16 h-10 rounded-lg overflow-hidden shrink-0">
                  <img src={c.thumbnail} alt={c.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-semibold truncate" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{c.title}</p>
                  <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{c.timestamp} · {c.duration}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {c.tags.map((tag) => (
                      <span key={tag} className="text-xs bg-white/5 text-gray-400 px-1.5 py-0.5 rounded" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{tag}</span>
                    ))}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-lg font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: c.color }}>{c.score}</div>
                </div>
              </button>
            ))}
          </div>

          {/* AI Insights */}
          {showInsights && (
            <div className="bg-[#0F1419] border border-[#00D9FF]/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#00D9FF]/15">
                  <i className="ri-brain-line text-[#00D9FF] text-xs" />
                </div>
                <span className="text-white text-xs font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>AI 선정 이유</span>
              </div>
              <p className="text-gray-400 text-xs leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                {clip.reason}
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500" style={{ fontFamily: "'Inter', sans-serif" }}>참여 급등 신호</span>
                  <span className="text-[#00D9FF]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{clip.peak}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500" style={{ fontFamily: "'Inter', sans-serif" }}>바이럴 예측 점수</span>
                  <span className="text-[#F59E0B] font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{clip.viralScore} / 10</span>
                </div>
              </div>

              {/* Viral score bar */}
              <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${clip.viralScore * 10}%`,
                    background: "linear-gradient(90deg, #F59E0B, #EF4444)",
                  }}
                />
              </div>
              <div className="flex justify-between text-gray-600 text-xs mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                <span>낮음</span><span>바이럴 가능성</span><span>높음</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
